import exceljs from "exceljs";
import { saveAs } from "file-saver";
import { useUiStore } from "src/components/main/uiStore";
//--------------------------------------------------------------------------------------------
import { useAuthStore } from "src/components/main/authStore";
import {
    mimeTypeMap, 
    MyDate, 
    simpleStatusColorCodExcelHex,
    defaultEmptyDateTime, 
    weekDays, 
    numberReducer, 
    weekIndexes
} from "src/components/common/functions";
import type { PayrollAssignment, PayrollDay, PayrollUserDays } from "./payroll";

type PayrollExportProps = {
    userEveryDayCalculatedData: PayrollUserDays[];
    filteredData: PayrollAssignment[];
};

export default function Export({
    userEveryDayCalculatedData, 
    filteredData
}: PayrollExportProps){
    const setShowLoader = useUiStore(state => state.setShowLoader);
    const curUser = useAuthStore(state => state.user);
    const exportFunc = () => {
            if(!filteredData.length){
                window.alert("No data to export.");
                return;
            }
            setShowLoader(true);
            const workbook = new exceljs.Workbook();
            workbook.creator = curUser?.userName || "";
            workbook.created = new Date();
            workbook.calcProperties.fullCalcOnLoad = true;
            const headers = ["", ...weekDays, "Total"]; 
            for(let totalI = 0; totalI < userEveryDayCalculatedData.length; totalI++){
            const testData = userEveryDayCalculatedData[totalI];
            let maxlength = 1;
            const worksheet = workbook.addWorksheet(testData.userName, {views: [{showGridLines: false}]});
            worksheet.state = "visible";
            worksheet.properties.defaultColWidth = 22.57;
            for(let z = 1; z < 10; z++){
                worksheet.mergeCells(1,2 * z - 1, 1,2 * z);
                worksheet.getCell(1,2 * z - 1).value = headers[z - 1];
                worksheet.getCell(1,2 * z - 1).alignment = { vertical: "middle", horizontal: "center" };
                worksheet.getCell(1,2 * z - 1).border = {
                    top: {style:"thin"},
                    left: {style:"thin"},
                    bottom: {style:"thin"},
                    right: {style:"thin"}
                }
            }
            for(const [numi, num] of weekIndexes.entries()){
                const filterData = testData.hours[num].jobs;
                const colData = [headers[numi + 1]];
                maxlength = Math.max(maxlength, filterData.length);
                for(let i = 0; i < filterData.length; i++){
                    const cell =  worksheet.getCell(2 + i * 7, 2 * numi + 3);
                    cell.value = `${filterData[i].projectNumber} - ${filterData[i].organizationName} - ${filterData[i].clientProjectNumber}`;
                    worksheet.mergeCells(2 + i * 7, 2 * numi + 3, 8 + i * 7, 2 * numi + 3);
                    cell.alignment = { vertical: "middle", horizontal: "center",  wrapText: true };
                    cell.border = {
                        top: {style:"thin"},
                        left: {style:"thin"},
                        bottom: {style:"thin"},
                        right: {style:"thin"}
                    }
                    cell.font = {            
                        color: { argb: simpleStatusColorCodExcelHex[filterData[i].AssignmentStatus] },
                        bold: true
                      };
                    colData.push(
                        filterData[i].preDriver,
                        filterData[i].travelStartTime === defaultEmptyDateTime.replace(/-/,"/")?"":filterData[i].travelStartTime, 
                        filterData[i].workStartTime === defaultEmptyDateTime.replace(/-/,"/")?"":filterData[i].workStartTime, 
                        filterData[i].hadLunch, 
                        filterData[i].workEndTime === defaultEmptyDateTime.replace(/-/,"/")?"":filterData[i].workEndTime, 
                        filterData[i].travelEndTime === defaultEmptyDateTime.replace(/-/,"/")?"":filterData[i].travelEndTime, 
                        filterData[i].postDriver
                    );
                }
                worksheet.getColumn(2 * numi + 4).values = colData;
            }
            const headerColData = [testData.userName];
            for(let ii = 0; ii < maxlength; ii++){
                worksheet.mergeCells(ii * 7 + 2, 1, ii * 7 + 8, 1); 
                worksheet.getCell(ii * 7 + 2, 1).value = `Project ${ii + 1}`;
                worksheet.getCell(ii * 7 + 2, 1).alignment = { vertical: "middle", horizontal: "center",  wrapText: true };
                worksheet.getCell(ii * 7 + 2, 1).border = {
                    top: {style:"thin"},
                    left: {style:"thin"},
                    bottom: {style:"thin"},
                    right: {style:"thin"}
                }
                worksheet.getCell(ii * 7 + 2, 2).value = "";
                headerColData.push(
                    "pre driver", 
                    "travel start", 
                    "work start", 
                    "lunch", 
                    "work end", 
                    "travel end", 
                    "post driver"
                );
            }
            headerColData.push("Travel", "Regular", "OT1", "OT2");
            worksheet.getColumn(2).values = headerColData;
            for(let iii = 0; iii < 7; iii++){
                worksheet.getCell(2 + 7 * maxlength, 4 + iii * 2).value = userEveryDayCalculatedData[totalI].hours[iii===6?0:(iii + 1)].travel;
                worksheet.getCell(3 + 7 * maxlength, 4 + iii * 2).value = userEveryDayCalculatedData[totalI].hours[iii===6?0:(iii + 1)].regular;
                worksheet.getCell(4 + 7 * maxlength, 4 + iii * 2).value = userEveryDayCalculatedData[totalI].hours[iii===6?0:(iii + 1)].ot1;
                worksheet.getCell(5 + 7 * maxlength, 4 + iii * 2).value = userEveryDayCalculatedData[totalI].hours[iii===6?0:(iii + 1)].ot2;
            }
            worksheet.getCell(2 + 7 * maxlength, 4 + 7 * 2).value = userEveryDayCalculatedData[totalI].hours.map((xx: PayrollDay) => xx.travel).reduce(numberReducer, 0);
            worksheet.getCell(3 + 7 * maxlength, 4 + 7 * 2).value = userEveryDayCalculatedData[totalI].hours.map((xx: PayrollDay) => xx.regular).reduce(numberReducer, 0);
            worksheet.getCell(4 + 7 * maxlength, 4 + 7 * 2).value = userEveryDayCalculatedData[totalI].hours.map((xx: PayrollDay) => xx.ot1).reduce(numberReducer, 0);
            worksheet.getCell(5 + 7 * maxlength, 4 + 7 * 2).value = userEveryDayCalculatedData[totalI].hours.map((xx: PayrollDay) => xx.ot2).reduce(numberReducer, 0);
        }
            workbook.xlsx.writeBuffer().then(buffer => {
                saveAs(new Blob([buffer], { type: mimeTypeMap.xlsx}), 
                `payroll export ${(new MyDate()).s()}.xlsx`);
                setShowLoader(false);
            })
        };
    return (
    <button type="button" className="btn btn-outline-success" onClick={exportFunc}>
        Export
    </button>
    )
}
